import React, { useState, useEffect } from 'react';
import { useCookies } from '../contexts/CookieContext';
import { useLanguage } from '../contexts/LanguageContext';

const Toggle = ({ active, disabled, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="relative rounded-full focus:outline-none focus:ring-2 focus:ring-[#0a84ae]/60"
  >
    <div className={`flex h-6 w-12 items-center rounded-full transition-colors ${active ? 'bg-[#0a84ae]' : 'bg-white/[0.18]'}`}>
      <div className={`h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${active ? 'translate-x-6' : 'translate-x-1'}`} />
    </div>
  </button>
);

const CookiePreferences = () => {
  const {
    showPreferences,
    setShowPreferences,
    cookiePreferences,
    saveCustomPreferences,
    acceptAllCookies,
    rejectAllCookies
  } = useCookies();
  const { t } = useLanguage();

  const [localPreferences, setLocalPreferences] = useState(cookiePreferences);

  useEffect(() => {
    setLocalPreferences(cookiePreferences);
  }, [cookiePreferences]);

  if (!showPreferences) return null;

  const handleToggle = (category) => {
    if (category === 'necessary') return;

    setLocalPreferences(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const handleSave = () => {
    saveCustomPreferences(localPreferences);
  };

  const handleClose = () => {
    setShowPreferences(false);
    setLocalPreferences(cookiePreferences);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={handleClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-[24px] border border-white/10 bg-[#0b0b0f] text-[#fffefa] shadow-2xl shadow-black/70"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-white/10 p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-md border border-white/10 bg-white/[0.06] text-[#0a84ae]">
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
              </svg>
            </div>
            <h2 className="text-xl font-normal text-white">{t('cookies.preferencesTitle')}</h2>
          </div>
          <button
            onClick={handleClose}
            className="rounded-md p-2 text-[#8d8d95] transition-colors hover:bg-white/[0.06] hover:text-white"
            aria-label="Cerrar"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-4 p-6">
          <div className="rounded-[24px] border border-white/10 bg-white/[0.04] p-4">
            <div className="mb-3 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <span className="h-2 w-2 rounded-full bg-[#0a84ae]" />
                <h4 className="font-medium text-white">{t('cookies.necessary')}</h4>
                <span className="rounded-full border border-[#0a84ae]/[0.35] bg-[#0a84ae]/10 px-2 py-1 text-xs font-medium text-[#c6efff]">
                  {t('cookies.alwaysActive')}
                </span>
              </div>
              <Toggle active disabled />
            </div>
            <p className="text-sm leading-6 text-[#b7b5c2]">
              {t('cookies.necessaryDesc')}
            </p>
          </div>

          <div className="rounded-[24px] border border-white/10 bg-white/[0.04] p-4">
            <div className="mb-3 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <span className={`h-2 w-2 rounded-full ${localPreferences.functional ? 'bg-[#0a84ae]' : 'bg-[#8d8d95]'}`} />
                <h4 className="font-medium text-white">{t('cookies.functional')}</h4>
              </div>
              <Toggle active={localPreferences.functional} onClick={() => handleToggle('functional')} />
            </div>
            <p className="text-sm leading-6 text-[#b7b5c2]">
              {t('cookies.functionalDesc')}
            </p>
          </div>
        </div>

        <div className="border-t border-white/10 p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-between">
            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                onClick={rejectAllCookies}
                className="min-h-[42px] rounded-md border border-white/10 bg-white/[0.04] px-4 text-sm font-medium text-[#d7d6de] transition-colors duration-200 hover:bg-white/[0.08] hover:text-white"
              >
                {t('cookies.rejectAll')}
              </button>
              <button
                onClick={acceptAllCookies}
                className="min-h-[42px] rounded-md border border-white/10 bg-white/[0.08] px-4 text-sm font-medium text-white transition-colors duration-200 hover:bg-white/[0.12]"
              >
                {t('cookies.acceptAll')}
              </button>
            </div>

            <button
              onClick={handleSave}
              className="min-h-[42px] rounded-md bg-white px-6 text-sm font-medium text-black shadow-[inset_0_-12px_20px_rgba(255,255,255,0.45),0_4px_14px_rgba(255,255,255,0.14)]"
            >
              {t('cookies.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePreferences;
